import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Scale, UploadCloud, FileWarning, RotateCcw, ShieldOff, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TABS = [
  { key: "opposing", label: "Opposing Discovery" },
  { key: "response", label: "Discovery Response" },
  { key: "withholding", label: "Withholding Log" },
];

export default function DiscoveryVault({ open, onClose, matterId, onChanged }) {
  const [tab, setTab] = useState("opposing");
  const [subcats, setSubcats] = useState([]);
  const [groups, setGroups] = useState([]);
  const [userEvidence, setUserEvidence] = useState([]);
  const [responses, setResponses] = useState({});
  const [withheld, setWithheld] = useState([]);
  const [uploadSub, setUploadSub] = useState("interrogatories");
  const [busy, setBusy] = useState(false);
  const [gateReason, setGateReason] = useState("");

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const cfg = await api.get("/discovery/config");
      setSubcats(cfg.data.subcategories || []);
      const opp = await api.get("/discovery/opposing");
      setGroups(opp.data.groups || []);
      const ev = await api.get(`/evidence${matterId ? `?matter_id=${matterId}` : ""}`);
      setUserEvidence((ev.data || []).filter((e) => e.vault_section !== "opposing_discovery"));
      const resp = await api.get("/discovery/responses");
      const map = {};
      (resp.data.responses || []).forEach((r) => { map[r.opposing_id] = r.evidence_ids || []; });
      setResponses(map);
      const wl = await api.get("/discovery/withholding-log");
      setWithheld(wl.data.items || []);
    } catch (e) { console.error("discovery load failed", e); }
  }, [open, matterId]);

  useEffect(() => { load(); }, [load]);

  const uploadOpposing = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("matter_id", matterId || "");
      fd.append("vault_section", "opposing_discovery");
      fd.append("discovery_subcategory", uploadSub);
      await api.post("/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Opposing discovery item added");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const toggleMap = async (opposingId, eid) => {
    const current = responses[opposingId] || [];
    const next = current.includes(eid) ? current.filter((x) => x !== eid) : [...current, eid];
    setResponses((p) => ({ ...p, [opposingId]: next }));
    try { await api.post("/discovery/response", { opposing_id: opposingId, evidence_ids: next }); }
    catch (err) { toast.error("Could not save mapping"); load(); }
  };

  const gateUnused = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/discovery/gate-unused", { reason: gateReason });
      toast.success(data.count ? `${data.count} item(s) withheld` : "No unused evidence to withhold");
      setGateReason("");
      await load();
      onChanged && onChanged();
      setTab("withholding");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not gate evidence"); }
    finally { setBusy(false); }
  };

  const restore = async (eid) => {
    try {
      await api.post(`/discovery/ungate/${eid}`);
      toast.success("Item restored");
      await load();
      onChanged && onChanged();
    } catch (err) { toast.error("Could not restore"); }
  };

  const usedIds = new Set(Object.values(responses).flat());
  const unusedCount = userEvidence.filter((e) => !usedIds.has(e.id) && !e.gated).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="discovery-vault">
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2">
            <Scale className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Discovery Center
          </DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            Keep opposing-party discovery separate from your evidence, map what responds to each request, and withhold anything not used.
          </DialogDescription>
        </DialogHeader>

        <div className="inline-flex rounded-md border overflow-hidden mb-3" style={{ borderColor: "var(--ek-border)" }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`discovery-tab-${t.key}`}
              className="px-3 py-1.5 text-xs font-medium transition-colors"
              style={{ background: tab === t.key ? "var(--ek-primary)" : "transparent", color: tab === t.key ? "#fff" : "var(--ek-text-secondary)" }}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {tab === "opposing" && (
            <div className="space-y-4" data-testid="discovery-opposing">
              <div className="flex items-center gap-2 flex-wrap rounded-md border p-3" style={{ borderColor: "var(--ek-border)" }}>
                <select value={uploadSub} onChange={(e) => setUploadSub(e.target.value)} data-testid="opposing-subcat-select"
                  className="rounded-md border px-2 py-1.5 text-sm bg-transparent" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text)" }}>
                  {subcats.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <label className="inline-flex items-center gap-2 text-sm rounded-md px-3 py-1.5 cursor-pointer" data-testid="opposing-upload-label" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                  <UploadCloud className="h-4 w-4" /> {busy ? "Uploading…" : "Add opposing item"}
                  <input type="file" className="hidden" onChange={uploadOpposing} disabled={busy} data-testid="opposing-upload-input" />
                </label>
              </div>
              {groups.map((g) => (
                <div key={g.key} data-testid={`opposing-group-${g.key}`}>
                  <p className="ek-mono-label mb-1">{g.label} · {g.items.length}</p>
                  {g.items.length === 0 ? (
                    <p className="text-xs" style={{ color: "var(--ek-text-muted)" }}>No items yet.</p>
                  ) : (
                    <div className="space-y-1">
                      {g.items.map((it) => (
                        <div key={it.id} className="text-sm border rounded-md px-3 py-2" style={{ borderColor: "var(--ek-border)" }} data-private>{it.filename}</div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "response" && (
            <div className="space-y-4" data-testid="discovery-response">
              {groups.filter((g) => g.items.length).length === 0 && (
                <p className="text-sm" style={{ color: "var(--ek-text-muted)" }}>Add opposing discovery items first, then map your evidence to each request.</p>
              )}
              {groups.map((g) => g.items.map((opp) => (
                <div key={opp.id} className="border rounded-md p-3" style={{ borderColor: "var(--ek-border)" }} data-testid={`response-item-${opp.id}`}>
                  <p className="text-sm font-medium mb-2" data-private><span className="ek-mono-label mr-2">{g.label}</span>{opp.filename}</p>
                  <p className="ek-mono-label mb-1">SELECT YOUR EVIDENCE THAT RESPONDS</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {userEvidence.length === 0 && <p className="text-xs" style={{ color: "var(--ek-text-muted)" }}>No user evidence in this case.</p>}
                    {userEvidence.map((ev) => {
                      const checked = (responses[opp.id] || []).includes(ev.id);
                      return (
                        <label key={ev.id} className="flex items-center gap-2 text-sm cursor-pointer rounded-md border px-2 py-1.5"
                          style={{ borderColor: checked ? "var(--ek-primary)" : "var(--ek-border)", background: checked ? "rgba(46,130,246,0.08)" : "transparent" }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleMap(opp.id, ev.id)} data-testid={`map-${opp.id}-${ev.id}`} />
                          <span className="truncate" data-private>{ev.filename}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )))}

              <div className="border rounded-md p-3" style={{ borderColor: "rgba(240,97,109,0.35)", background: "rgba(240,97,109,0.05)" }}>
                <p className="text-sm font-medium flex items-center gap-2"><FileWarning className="h-4 w-4" style={{ color: "var(--ek-error)" }} /> Withhold unused evidence</p>
                <p className="text-xs mt-1" style={{ color: "var(--ek-text-secondary)" }}>
                  {unusedCount} user-evidence item(s) are not mapped to any request. Withholding removes them from every court report (reversible from the Withholding Log).
                </p>
                <input value={gateReason} onChange={(e) => setGateReason(e.target.value)} data-testid="gate-reason-input"
                  placeholder="Reason for withholding (optional)"
                  className="w-full rounded-md border px-3 py-2 text-sm bg-transparent mt-2" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text)" }} />
                <Button onClick={gateUnused} disabled={busy} data-testid="gate-unused-btn" className="rounded-md mt-2" style={{ background: "var(--ek-error)", color: "#fff" }}>
                  <ShieldOff className="h-4 w-4 mr-2" /> Withhold {unusedCount} unused item(s)
                </Button>
              </div>
            </div>
          )}

          {tab === "withholding" && (
            <div className="space-y-2" data-testid="discovery-withholding">
              {withheld.length === 0 ? (
                <p className="text-sm flex items-center gap-2" style={{ color: "var(--ek-text-muted)" }}><CheckCircle2 className="h-4 w-4" style={{ color: "var(--ek-secure)" }} /> Nothing is currently withheld.</p>
              ) : withheld.map((it) => (
                <div key={it.id} className="flex items-center justify-between border rounded-md px-3 py-2" style={{ borderColor: "var(--ek-border)" }} data-testid={`withheld-${it.id}`}>
                  <div>
                    <p className="text-sm" data-private>{it.filename}</p>
                    <p className="text-xs" style={{ color: "var(--ek-text-muted)" }}>{it.gated_reason || "No reason recorded"}</p>
                  </div>
                  <Button variant="outline" onClick={() => restore(it.id)} data-testid={`restore-${it.id}`} className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
                    <RotateCcw className="h-4 w-4 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
